// Email Notification Types and Templates
import type { Timestamp } from 'firebase/firestore';

// Notification types
export type NotificationType =
  | 'ticket_created'        // New ticket submitted by customer
  | 'ticket_reply_agent'    // Agent replied to customer
  | 'ticket_reply_customer' // Customer replied to ticket
  | 'ticket_assigned'       // Ticket assigned to agent
  | 'ticket_status_changed' // Ticket status changed
  | 'ticket_resolved'       // Ticket marked as resolved
  | 'team_invite'           // Team member invited
  | 'feature_status_update' // Feature request status changed
  | 'feature_comment';      // Comment on feature request

// Notification status
export type NotificationStatus = 'pending' | 'sent' | 'failed' | 'skipped';

// Email notification record (stored in Firestore)
export interface EmailNotification {
  id: string;
  type: NotificationType;
  status: NotificationStatus;

  // Recipient info
  recipientEmail: string;
  recipientName?: string;

  // Organization context
  organizationId: string;
  organizationName: string;
  projectId?: string;
  projectName?: string;

  // Related entity
  entityType: 'ticket' | 'feature' | 'member';
  entityId: string;

  // Email content
  subject: string;
  htmlBody: string;
  textBody: string;

  // Reply link (for ticket notifications)
  replyUrl?: string;

  // Metadata
  createdAt: Timestamp;
  sentAt?: Timestamp;
  error?: string;

  // Prevent duplicates
  idempotencyKey: string;
}

// Template data for different notification types
export interface TicketNotificationData {
  ticketId: string;
  ticketNumber: number;
  ticketSubject: string;
  ticketDescription?: string;
  submitterName: string;
  submitterEmail: string;
  agentName?: string;
  replyContent?: string;
  newStatus?: string;
  portalUrl: string;
  replyUrl: string;
}

export interface TeamInviteNotificationData {
  inviterName: string;
  organizationName: string;
  role: string;
  inviteUrl: string;
  expiresAt: Date;
}

export interface FeatureNotificationData {
  featureId: string;
  featureTitle: string;
  newStatus?: string;
  commentContent?: string;
  authorName: string;
  portalUrl: string;
}

// Email template generator
export const generateEmailContent = {
  // Customer receives when agent replies
  ticketReplyToCustomer: (data: TicketNotificationData, branding: { name: string; primaryColor: string }) => ({
    subject: `Re: [#${data.ticketNumber}] ${data.ticketSubject}`,
    html: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
  <div style="border-bottom: 3px solid ${branding.primaryColor}; padding-bottom: 20px; margin-bottom: 20px;">
    <h2 style="margin: 0; color: ${branding.primaryColor};">${branding.name}</h2>
  </div>

  <p>Hi ${data.submitterName || 'there'},</p>

  <p><strong>${data.agentName}</strong> has replied to your support request:</p>

  <div style="background: #f5f5f5; border-left: 4px solid ${branding.primaryColor}; padding: 15px; margin: 20px 0; border-radius: 0 8px 8px 0;">
    <p style="margin: 0; white-space: pre-wrap;">${data.replyContent}</p>
  </div>

  <div style="margin: 30px 0;">
    <a href="${data.replyUrl}" style="display: inline-block; background: ${branding.primaryColor}; color: white; text-decoration: none; padding: 12px 24px; border-radius: 6px; font-weight: 500;">
      View & Reply
    </a>
  </div>

  <p style="color: #666; font-size: 14px;">
    Original request: "${data.ticketSubject}"<br>
    Ticket #${data.ticketNumber}
  </p>

  <hr style="border: none; border-top: 1px solid #eee; margin: 30px 0;">

  <p style="color: #999; font-size: 12px;">
    You're receiving this email because you submitted a support request to ${branding.name}.<br>
    <a href="${data.replyUrl}" style="color: #666;">Click here</a> to view the full conversation.
  </p>
</body>
</html>
    `.trim(),
    text: `
Hi ${data.submitterName || 'there'},

${data.agentName} has replied to your support request:

---
${data.replyContent}
---

View & Reply: ${data.replyUrl}

Original request: "${data.ticketSubject}"
Ticket #${data.ticketNumber}

---
You're receiving this email because you submitted a support request to ${branding.name}.
    `.trim(),
  }),

  // Agent receives when customer replies
  ticketReplyToAgent: (data: TicketNotificationData, branding: { name: string; primaryColor: string }) => ({
    subject: `[Customer Reply] #${data.ticketNumber}: ${data.ticketSubject}`,
    html: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
  <div style="border-bottom: 3px solid ${branding.primaryColor}; padding-bottom: 20px; margin-bottom: 20px;">
    <h2 style="margin: 0; color: ${branding.primaryColor};">${branding.name} Support</h2>
  </div>

  <p><strong>${data.submitterName || data.submitterEmail}</strong> replied to ticket #${data.ticketNumber}:</p>

  <div style="background: #f5f5f5; border-left: 4px solid #666; padding: 15px; margin: 20px 0; border-radius: 0 8px 8px 0;">
    <p style="margin: 0; white-space: pre-wrap;">${data.replyContent}</p>
  </div>

  <div style="margin: 30px 0;">
    <a href="${data.portalUrl}" style="display: inline-block; background: ${branding.primaryColor}; color: white; text-decoration: none; padding: 12px 24px; border-radius: 6px; font-weight: 500;">
      View in Dashboard
    </a>
  </div>

  <p style="color: #666; font-size: 14px;">
    Subject: "${data.ticketSubject}"<br>
    Customer: ${data.submitterEmail}
  </p>
</body>
</html>
    `.trim(),
    text: `
${data.submitterName || data.submitterEmail} replied to ticket #${data.ticketNumber}:

---
${data.replyContent}
---

View in Dashboard: ${data.portalUrl}

Subject: "${data.ticketSubject}"
Customer: ${data.submitterEmail}
    `.trim(),
  }),

  // New ticket notification to agents
  newTicketNotification: (data: TicketNotificationData, branding: { name: string; primaryColor: string }) => ({
    subject: `[New Ticket] #${data.ticketNumber}: ${data.ticketSubject}`,
    html: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
  <div style="border-bottom: 3px solid ${branding.primaryColor}; padding-bottom: 20px; margin-bottom: 20px;">
    <h2 style="margin: 0; color: ${branding.primaryColor};">${branding.name} Support</h2>
  </div>

  <p>New support ticket received:</p>

  <div style="background: #f9f9f9; padding: 20px; border-radius: 8px; margin: 20px 0;">
    <p style="margin: 0 0 10px 0;"><strong>Subject:</strong> ${data.ticketSubject}</p>
    <p style="margin: 0 0 10px 0;"><strong>From:</strong> ${data.submitterName || 'Unknown'} &lt;${data.submitterEmail}&gt;</p>
    <p style="margin: 0 0 10px 0;"><strong>Ticket #:</strong> ${data.ticketNumber}</p>
  </div>

  <div style="background: #f5f5f5; border-left: 4px solid #666; padding: 15px; margin: 20px 0; border-radius: 0 8px 8px 0;">
    <p style="margin: 0; white-space: pre-wrap;">${data.ticketDescription}</p>
  </div>

  <div style="margin: 30px 0;">
    <a href="${data.portalUrl}" style="display: inline-block; background: ${branding.primaryColor}; color: white; text-decoration: none; padding: 12px 24px; border-radius: 6px; font-weight: 500;">
      View & Respond
    </a>
  </div>
</body>
</html>
    `.trim(),
    text: `
New support ticket received:

Subject: ${data.ticketSubject}
From: ${data.submitterName || 'Unknown'} <${data.submitterEmail}>
Ticket #: ${data.ticketNumber}

---
${data.ticketDescription}
---

View & Respond: ${data.portalUrl}
    `.trim(),
  }),

  // Ticket resolved notification to customer
  ticketResolvedNotification: (data: TicketNotificationData, branding: { name: string; primaryColor: string }) => ({
    subject: `[Resolved] #${data.ticketNumber}: ${data.ticketSubject}`,
    html: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
  <div style="border-bottom: 3px solid ${branding.primaryColor}; padding-bottom: 20px; margin-bottom: 20px;">
    <h2 style="margin: 0; color: ${branding.primaryColor};">${branding.name}</h2>
  </div>

  <p>Hi ${data.submitterName || 'there'},</p>

  <p>Your support request has been marked as <strong style="color: #22c55e;">resolved</strong>.</p>

  <div style="background: #f0fdf4; border-left: 4px solid #22c55e; padding: 15px; margin: 20px 0; border-radius: 0 8px 8px 0;">
    <p style="margin: 0;"><strong>Ticket #${data.ticketNumber}:</strong> ${data.ticketSubject}</p>
  </div>

  <p>If you have any further questions or if this issue wasn't fully resolved, feel free to reply:</p>

  <div style="margin: 30px 0;">
    <a href="${data.replyUrl}" style="display: inline-block; background: ${branding.primaryColor}; color: white; text-decoration: none; padding: 12px 24px; border-radius: 6px; font-weight: 500;">
      View Ticket
    </a>
  </div>

  <p>Thank you for using ${branding.name}!</p>

  <hr style="border: none; border-top: 1px solid #eee; margin: 30px 0;">

  <p style="color: #999; font-size: 12px;">
    You're receiving this email because you submitted a support request to ${branding.name}.
  </p>
</body>
</html>
    `.trim(),
    text: `
Hi ${data.submitterName || 'there'},

Your support request has been marked as RESOLVED.

Ticket #${data.ticketNumber}: ${data.ticketSubject}

If you have any further questions or if this issue wasn't fully resolved, feel free to reply:
${data.replyUrl}

Thank you for using ${branding.name}!
    `.trim(),
  }),

  // Team invite notification
  teamInviteNotification: (data: TeamInviteNotificationData, branding: { name: string; primaryColor: string }) => ({
    subject: `You've been invited to join ${data.organizationName} on ${branding.name}`,
    html: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
  <div style="border-bottom: 3px solid ${branding.primaryColor}; padding-bottom: 20px; margin-bottom: 20px;">
    <h2 style="margin: 0; color: ${branding.primaryColor};">${branding.name}</h2>
  </div>

  <p>Hi,</p>

  <p><strong>${data.inviterName}</strong> has invited you to join <strong>${data.organizationName}</strong> as a <strong>${data.role}</strong>.</p>

  <div style="margin: 30px 0;">
    <a href="${data.inviteUrl}" style="display: inline-block; background: ${branding.primaryColor}; color: white; text-decoration: none; padding: 12px 24px; border-radius: 6px; font-weight: 500;">
      Accept Invitation
    </a>
  </div>

  <p style="color: #666; font-size: 14px;">
    This invitation will expire in 7 days.
  </p>

  <hr style="border: none; border-top: 1px solid #eee; margin: 30px 0;">

  <p style="color: #999; font-size: 12px;">
    If you didn't expect this invitation, you can ignore this email.
  </p>
</body>
</html>
    `.trim(),
    text: `
Hi,

${data.inviterName} has invited you to join ${data.organizationName} as a ${data.role}.

Accept Invitation: ${data.inviteUrl}

This invitation will expire in 7 days.

---
If you didn't expect this invitation, you can ignore this email.
    `.trim(),
  }),
};

// Helper to generate idempotency key
// Uses type + entityId for deterministic deduplication (same event always produces same key)
export const generateIdempotencyKey = (type: NotificationType, entityId: string): string => {
  return `${type}_${entityId}`;
};
